import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/api";
import ExerciseCard from "@/components/ExerciseCard";
import RestTimer from "@/components/RestTimer";
import { toast } from "sonner";
import { Plus, X, ChevronLeft } from "lucide-react";

function initSet(sets) { return Array.from({ length: sets }, () => ({ w: null, reps: null, logged: false, skipped: false })); }

export default function Workout() {
  const nav = useNavigate();
  const [program, setProgram] = useState(null);
  const [library, setLibrary] = useState(null);
  const [session, setSession] = useState({}); // exerciseKey -> {sets:[], skippedEntirely}
  const [startTs] = useState(() => Date.now());
  const [replaceFor, setReplaceFor] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [rest, setRest] = useState(null); // { seconds, label }

  useEffect(() => {
    api.get("/program").then((r) => {
      setProgram(r.data);
      const init = {};
      r.data.split[r.data.cursor].exercises.forEach((ex) => {
        init[ex.exerciseKey] = { sets: initSet(ex.sets), skippedEntirely: false };
      });
      setSession(init);
    });
    api.get("/library").then((r) => setLibrary(r.data));
  }, []);

  const day = program ? program.split[program.cursor] : null;
  const exercises = day?.exercises || [];

  const firstIncompleteKey = useMemo(() => {
    for (const ex of exercises) {
      const st = session[ex.exerciseKey];
      if (!st || st.skippedEntirely) continue;
      const done = st.sets.every((s) => s.logged || s.skipped);
      if (!done) return ex.exerciseKey;
    }
    return null;
  }, [exercises, session]);

  const anyLogged = Object.values(session).some((st) => st.sets.some((s) => s.logged));
  const totalDurationSec = Math.floor((Date.now() - startTs) / 1000);

  const finish = () => {
    if (!anyLogged) { toast.error("Log at least one set."); return; }
    const payload = {
      durationSec: totalDurationSec,
      exercises: exercises.map((ex) => {
        const st = session[ex.exerciseKey];
        return {
          exerciseKey: ex.exerciseKey,
          skippedEntirely: st.skippedEntirely,
          sets: st.sets.filter((s) => s.logged || s.skipped).map((s) => ({ w: Number(s.w || 0), reps: Number(s.reps || 0), skipped: !!s.skipped })),
        };
      }),
    };
    nav("/session/finish", { state: { payload, exercises } });
  };

  const skipExercise = (ex) => {
    setSession((s) => ({ ...s, [ex.exerciseKey]: { ...s[ex.exerciseKey], skippedEntirely: true } }));
    toast("Skipped for today.");
  };

  const doReplace = async (newKey) => {
    if (!replaceFor) return;
    try {
      await api.post("/program/replace", { dayIdx: program.cursor, exerciseKey: replaceFor.exerciseKey, replacementKey: newKey });
      toast.success("Replaced.");
      const { data } = await api.get("/program");
      setProgram(data);
      const init = { ...session };
      const newEx = data.split[data.cursor].exercises.find((e) => e.exerciseKey === newKey);
      delete init[replaceFor.exerciseKey];
      init[newKey] = { sets: initSet(newEx.sets), skippedEntirely: false };
      setSession(init);
      setReplaceFor(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const doAdd = async (key) => {
    try {
      await api.post("/program/add", { dayIdx: program.cursor, exerciseKey: key });
      const { data } = await api.get("/program");
      setProgram(data);
      const newEx = data.split[data.cursor].exercises.find((e) => e.exerciseKey === key);
      setSession((s) => ({ ...s, [key]: { sets: initSet(newEx.sets), skippedEntirely: false } }));
      setAddOpen(false);
      toast.success("Added.");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!program) return <div className="p-6 text-t3">Loading…</div>;

  const dayKeys = new Set(exercises.map((e) => e.exerciseKey));
  const focus = day.focus;
  const CORE_KEYS = library?.core || [];
  const pool = library ? [
    ...(library.dayLists[focus] || []),
    ...exercises.flatMap((e) => e.subs || []),
    ...CORE_KEYS,
  ].filter((k, i, a) => a.indexOf(k) === i && !dayKeys.has(k)) : [];

  return (
    <div className="min-h-screen bg-app max-w-md mx-auto px-5 pt-8 pb-6">
      <div className="flex items-center justify-between">
        <button className="stepper" onClick={() => nav(-1)} data-testid="workout-back"><ChevronLeft size={18} /></button>
        <div className="text-center">
          <div className="overline">Round {program.round} · {program.cursor + 1}/{program.split.length}</div>
          <h1 className="font-display text-2xl uppercase mt-1">{day.focus}</h1>
        </div>
        <div className="text-right">
          <div className="overline">Est</div>
          <div className="font-mono tabular text-sm">{day.estMinutes}m</div>
        </div>
      </div>

      {program.warmups > 0 && (
        <div className="mt-4 card p-3 flex items-start gap-3" style={{ background: "rgba(184,134,11,0.06)", borderColor: "rgba(184,134,11,0.35)" }} data-testid="workout-deload-banner">
          <span className="text-[10px] uppercase tracking-[0.22em] px-2 py-1 rounded-full mt-0.5" style={{ background: "rgba(184,134,11,0.16)", color: "var(--amber)" }}>Warm-up</span>
          <div className="text-xs text-t2 leading-relaxed">
            You've been away. Weights are shown at 60% for {program.warmups} more session{program.warmups > 1 ? "s" : ""}. Your saved progress isn't touched.
          </div>
        </div>
      )}

      <div className="mt-6 space-y-4">
        {exercises.map((ex) => {
          const st = session[ex.exerciseKey];
          if (!st) return null;
          if (st.skippedEntirely) return (
            <div key={ex.exerciseKey} className="card p-4 flex items-center justify-between text-t3 text-sm">
              <span>{ex.name} · skipped</span>
              <button className="btn btn-ghost text-xs" onClick={() => setSession((s) => ({ ...s, [ex.exerciseKey]: { ...s[ex.exerciseKey], skippedEntirely: false } }))}>Undo</button>
            </div>
          );
          return (
            <ExerciseCard
              key={ex.exerciseKey}
              ex={ex}
              state={st}
              onChange={(next) => setSession((s) => ({ ...s, [ex.exerciseKey]: next }))}
              isFirstIncomplete={firstIncompleteKey === ex.exerciseKey}
              onReplace={setReplaceFor}
              onSkip={skipExercise}
              onSetLogged={({ exercise, setIndex }) => setRest({ seconds: exercise.rest || 75, label: exercise.name, key: `${exercise.exerciseKey}-${setIndex}-${Date.now()}` })}
            />
          );
        })}

        <button className="w-full btn btn-outline" onClick={() => setAddOpen(true)} data-testid="workout-add-ex">
          <Plus size={16} /> Add exercise
        </button>
      </div>

      <button onClick={finish} className="btn btn-primary w-full mt-8" data-testid="workout-finish">Finish workout</button>

      {rest && (
        <RestTimer
          key={rest.key || rest.label}
          seconds={rest.seconds}
          label={rest.label}
          onDone={() => setRest(null)}
        />
      )}

      {/* Replace modal */}
      {replaceFor && library && (
        <Modal onClose={() => setReplaceFor(null)} title={`Replace ${replaceFor.name}`}>
          <div className="space-y-2">
            {(replaceFor.subs || []).filter((k) => !dayKeys.has(k)).map((k) => (
              <button key={k} onClick={() => doReplace(k)} className="w-full card p-3 text-left hover:border-hair" data-testid={`replace-opt-${k}`}>
                <div className="font-display uppercase">{library.exercises[k].name}</div>
                <div className="text-xs text-t3 mt-1 capitalize">{library.exercises[k].anchor} · {library.exercises[k].muscle}</div>
              </button>
            ))}
            {(replaceFor.subs || []).filter((k) => !dayKeys.has(k)).length === 0 && <div className="text-t3 text-sm">No available replacements.</div>}
          </div>
        </Modal>
      )}

      {/* Add modal */}
      {addOpen && library && (
        <Modal onClose={() => setAddOpen(false)} title="Add exercise">
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {pool.map((k) => (
              <button key={k} onClick={() => doAdd(k)} className="w-full card p-3 text-left hover:border-hair" data-testid={`add-opt-${k}`}>
                <div className="font-display uppercase">{library.exercises[k].name}</div>
                <div className="text-xs text-t3 mt-1 capitalize">{library.exercises[k].anchor} · {library.exercises[k].muscle}</div>
              </button>
            ))}
            {pool.length === 0 && <div className="text-t3 text-sm">Nothing left to add.</div>}
          </div>
        </Modal>
      )}
    </div>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" style={{ background: "rgba(0,0,0,0.7)" }} onClick={onClose}>
      <div className="bg-card border-hair rounded-t-3xl sm:rounded-3xl p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-lg uppercase">{title}</div>
          <button className="stepper" onClick={onClose} data-testid="modal-close"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
